// src/pages/Newsboard.jsx
import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ref, listAll, getDownloadURL } from "firebase/storage";
import { storage } from "../firebase";
import { ChevronLeft, ChevronRight } from "lucide-react";

function Newsboard() {
  const [companyInfo, setCompanyInfo] = useState({});
  const [images, setImages] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);

  const API_BASE_URL = "https://api.stinteportal.co";

  // =============================
  // Fetch company info
  // =============================
  useEffect(() => {
    const fetchCompanyInfo = async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/api/company-info`);

        let data = res.data;

        // 🔥 FIX: convert relative logo path → absolute
        if (data.logo_url && data.logo_url.startsWith("/")) {
          data.logo_url = `${API_BASE_URL}${data.logo_url}`;
        }

        console.log("✅ Company info response:", data);
        console.log("🖼 Logo URL:", data.logo_url);

        setCompanyInfo(data);
      } catch (err) {
        console.error("❌ Error fetching company info:", err);
      }
    };

    fetchCompanyInfo();
  }, []);

  // =============================
  // Fetch images from Firebase
  // =============================
  useEffect(() => {
    const fetchImages = async () => {
      setLoading(true);
      try {
        const folderRef = ref(storage, "newsboard/");
        const result = await listAll(folderRef);

        const urls = await Promise.all(
          result.items.map((item) => getDownloadURL(item))
        );

        setImages(urls);
      } catch (err) {
        console.error("❌ Firebase error loading images:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchImages();
  }, []);

  const nextImage = () => {
    setCurrentIndex((prev) => (prev + 1) % images.length);
  };

  const prevImage = () => {
    setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  return (
    <div className="p-4 md:p-6 bg-gray-50 min-h-screen font-sans">
      {/* === Header / Company Info === */}
      <section className="mb-6 md:mb-8 bg-white p-4 rounded shadow">
        <div className="flex flex-col items-center mb-4">
          {companyInfo.logo_url && (
            <Link to="/dashboard">
              <img
                src={companyInfo.logo_url}
                alt="Company Logo"
                className="h-16 mb-2 cursor-pointer"
                onLoad={(e) =>
                  console.log("✅ Logo loaded:", e.target.src)
                }
                onError={(e) =>
                  console.error("❌ Logo failed:", e.target.src)
                }
              />
            </Link>
          )}

          <h2 className="text-xl md:text-2xl font-bold text-center">
            Company Newsboard
          </h2>
        </div>

        <p><strong>Name:</strong> {companyInfo.name || "N/A"}</p>
        <p>
          <strong>Address:</strong>{" "}
          {companyInfo.address ? (
            <a
              href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                companyInfo.address
              )}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:underline"
            >
              {companyInfo.address}
            </a>
          ) : (
            "N/A"
          )}
        </p>
        <p><strong>Phone:</strong> {companyInfo.phone || "N/A"}</p>
        <p>
          <strong>Email:</strong>{" "}
          {companyInfo.email ? (
            <a
              href={`mailto:${companyInfo.email}`}
              className="text-blue-600 hover:underline"
            >
              {companyInfo.email}
            </a>
          ) : (
            "N/A"
          )}
        </p>
      </section>

      {/* === Gallery / Slideshow === */}
      <section className="relative max-w-4xl mx-auto">
        {loading ? (
          <p className="text-gray-500 text-center py-20">Loading images...</p>
        ) : images.length === 0 ? (
          <p className="text-gray-500 text-center py-20">
            No images uploaded yet.
          </p>
        ) : (
          <>
            <img
              src={images[currentIndex]}
              alt={`Newsboard ${currentIndex + 1}`}
              className="w-full max-h-[60vh] md:max-h-[80vh] object-contain rounded shadow-lg"
            />

            <button
              onClick={prevImage}
              className="absolute left-2 md:left-0 top-1/2 -translate-y-1/2 p-2 md:p-3 text-white bg-black/50 rounded-r"
            >
              <ChevronLeft size={32} />
            </button>

            <button
              onClick={nextImage}
              className="absolute right-2 md:right-0 top-1/2 -translate-y-1/2 p-2 md:p-3 text-white bg-black/50 rounded-l"
            >
              <ChevronRight size={32} />
            </button>

            <div className="flex justify-center gap-2 mt-4">
              {images.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setCurrentIndex(idx)}
                  className={`w-3 h-3 rounded-full ${
                    idx === currentIndex ? "bg-blue-600" : "bg-gray-400"
                  }`}
                />
              ))}
            </div>
          </>
        )}
      </section>
    </div>
  );
}

export default Newsboard;
